'use client';

import * as React from 'react';

/**
 * Toggles the experiments panel on Ctrl+Shift+Alt+D.
 *
 * Matched on `event.code` rather than `event.key`: on macOS, Option rewrites the
 * character, so Alt+Shift+D arrives as `Î` and a `key` comparison would never fire.
 *
 * The three-modifier chord is deliberate — this is reachable on every page, so it has to
 * be something no one hits by accident, and it avoids Ctrl+Shift+C (Chrome's inspector)
 * and Ctrl+Shift+K (Firefox's console).
 *
 * `useKeyPress` in `@hooks` is not usable here — it returns a handler prop for a single
 * element, whereas this needs a document-level listener.
 */
export const useExperimentsPanelHotkey = (): {
  isOpen: boolean;
  close: () => void;
} => {
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.ctrlKey &&
        event.shiftKey &&
        event.altKey &&
        event.code === 'KeyD'
      ) {
        event.preventDefault();
        setIsOpen((open) => !open);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const close = React.useCallback(() => setIsOpen(false), []);

  return { isOpen, close };
};
